import { PayloadAction } from '@reduxjs/toolkit'
import { GridItemPosition, GridItemSize } from 'Vendor/Utils/StudioTypes'
import { generateTempUUID, LayoutItem, StudioState } from '../initialState'
import {
    createMobileLayoutItemFromDesktop,
    toMobilePosition,
    toMobileSize,
} from './mobileLayoutUtils'

function getPage(state: StudioState, pageId: string) {
    if (!pageId) return null
    const page = state.pages[pageId]
    if (!page) return null
    return page
}

function normalizeZIndex(items: Record<string, LayoutItem>) {
    const sorted = Object.values(items)
        .sort((a, b) => a.zIndex - b.zIndex)

    sorted.forEach((item, index) => {
        items[item.id].zIndex = index
    })
}

export const layoutReducers = {
    itemMoved: (
        state: StudioState,
        action: PayloadAction<{
            pageId: string
            itemId: string
            newPosition: GridItemPosition
        }>,
    ) => {
        const { pageId, itemId, newPosition } = action.payload
        const page = getPage(state, pageId)
        if (!page) return

        const activeLayout = page.layouts[state.responsiveMode]
        if (activeLayout.items[itemId]) {
            activeLayout.items[itemId].position = newPosition
        }

        if (state.responsiveMode === 'desktop' && page.layouts.mobile.items[itemId]) {
            const desktopItem = page.layouts.desktop.items[itemId]
            if (desktopItem) {
                const mobileSize = toMobileSize(desktopItem.size)
                page.layouts.mobile.items[itemId] = {
                    ...page.layouts.mobile.items[itemId],
                    position: toMobilePosition(newPosition, mobileSize.col_span),
                    size: mobileSize,
                }
            }
        }
    },

    itemResized: (
        state: StudioState,
        action: PayloadAction<{
            pageId: string
            itemId: string
            newPosition: GridItemPosition
            newSize: GridItemSize
        }>,
    ) => {
        const { pageId, itemId, newPosition, newSize } = action.payload
        const page = getPage(state, pageId)
        if (!page) return

        const activeLayout = page.layouts[state.responsiveMode]
        if (activeLayout.items[itemId]) {
            activeLayout.items[itemId].position = newPosition
            activeLayout.items[itemId].size = newSize
        }

        if (state.responsiveMode === 'desktop' && page.layouts.mobile.items[itemId]) {
            const mobileSize = toMobileSize(newSize)
            page.layouts.mobile.items[itemId] = {
                ...page.layouts.mobile.items[itemId],
                position: toMobilePosition(newPosition, mobileSize.col_span),
                size: mobileSize,
            }
        }
    },

    bringForward: (
        state: StudioState,
        action: PayloadAction<{ pageId: string; itemId: string }>
    ) => {
        const { pageId, itemId } = action.payload
        const page = getPage(state, pageId)
        if (!page) return

        const activeLayout = page.layouts[state.responsiveMode]

        // 🔹 Normalize before swapping
        normalizeZIndex(activeLayout.items)

        const sortedItems = Object.values(activeLayout.items)
            .sort((a, b) => a.zIndex - b.zIndex)
        const currentIndex = sortedItems.findIndex((item) => item.id === itemId)

        if (currentIndex < sortedItems.length - 1) {
            const currentItem = sortedItems[currentIndex]
            const nextItem = sortedItems[currentIndex + 1]

            const temp = currentItem.zIndex
            currentItem.zIndex = nextItem.zIndex
            nextItem.zIndex = temp
        }

        if (state.responsiveMode === 'desktop') {
            const desktopItems = page.layouts.desktop.items
            const mobileItems = page.layouts.mobile.items
            Object.keys(desktopItems).forEach((id) => {
                if (mobileItems[id]) {
                    mobileItems[id].zIndex = desktopItems[id].zIndex
                }
            })
        }
    },

    sendBackward: (
        state: StudioState,
        action: PayloadAction<{ pageId: string; itemId: string }>
    ) => {
        const { pageId, itemId } = action.payload
        const page = getPage(state, pageId)
        if (!page) return

        const activeLayout = page.layouts[state.responsiveMode]

        // 🔹 Normalize before swapping
        normalizeZIndex(activeLayout.items)

        const sortedItems = Object.values(activeLayout.items)
            .sort((a, b) => a.zIndex - b.zIndex)
        const currentIndex = sortedItems.findIndex((item) => item.id === itemId)

        if (currentIndex > 0) {  // must be > 0
            const currentItem = sortedItems[currentIndex]
            const prevItem = sortedItems[currentIndex - 1]

            const temp = currentItem.zIndex
            currentItem.zIndex = prevItem.zIndex
            prevItem.zIndex = temp
        }

        if (state.responsiveMode === 'desktop') {
            const desktopItems = page.layouts.desktop.items
            const mobileItems = page.layouts.mobile.items
            Object.keys(desktopItems).forEach((id) => {
                if (mobileItems[id]) {
                    mobileItems[id].zIndex = desktopItems[id].zIndex
                }
            })
        }
    },

    itemDuplicated: (
        state: StudioState,
        action: PayloadAction<{ pageId: string; itemId: string }>,
    ) => {
        const { pageId, itemId } = action.payload

        const page = state.pages[pageId]
        if (!page) {
            return
        }

        const originalDesktopItem = page.layouts.desktop.items[itemId]
        const originalMobileItem = page.layouts.mobile.items[itemId]

        if (originalDesktopItem && originalMobileItem) {
            const newInstanceId = generateTempUUID()
            const maxZIndex =
                Math.max(
                    0,
                    ...Object.values(page.layouts.desktop.items).map(
                        (i) => i.zIndex,
                    ),
                ) + 1

            const newDesktopItem: LayoutItem = {
                ...originalDesktopItem,
                id: newInstanceId,
                position: {
                    ...originalDesktopItem.position,
                    col_start: originalDesktopItem.position.col_start + 1,
                    row_start: originalDesktopItem.position.row_start + 1,
                },
                zIndex: maxZIndex,
            }

            page.layouts.desktop.items[newInstanceId] = newDesktopItem
            page.layouts.desktop.itemIds.push(newInstanceId)

            const newMobileItem: LayoutItem = {
                ...originalMobileItem,
                id: newInstanceId,
                position: {
                    ...originalMobileItem.position,
                    col_start:
                        (originalMobileItem.position?.col_start || 1) + 1,
                    row_start:
                        (originalMobileItem.position?.row_start || 1) + 1,
                },
                zIndex: maxZIndex,
            }
            page.layouts.mobile.items[newInstanceId] = newMobileItem
            page.layouts.mobile.itemIds.push(newInstanceId)

            page.selectedItemIds = [newInstanceId]
        }
    },

    itemGroupDuplicated: (
        state: StudioState,
        action: PayloadAction<{ pageId: string; itemIds: string[] }>
    ) => {
        const { pageId, itemIds } = action.payload
        const page = state.pages[pageId]
        if (!page || itemIds.length === 0) return

        const desktop = page.layouts.desktop
        const mobile = page.layouts.mobile

        const maxZIndex =
            Math.max(0, ...Object.values(desktop.items).map(i => i.zIndex)) + 1

        const newIds: string[] = []

        const OFFSET = { col: 1, row: 1 }

        for (const itemId of itemIds) {
            const dItem = desktop.items[itemId]
            const mItem = mobile.items[itemId]
            if (!dItem || !mItem) continue

            const newId = generateTempUUID()
            newIds.push(newId)

            desktop.items[newId] = {
                ...dItem,
                id: newId,
                position: {
                    ...dItem.position,
                    col_start: dItem.position.col_start + OFFSET.col,
                    row_start: dItem.position.row_start + OFFSET.row,
                },
                zIndex: maxZIndex,
            }

            desktop.itemIds.push(newId)

            mobile.items[newId] = {
                ...mItem,
                id: newId,
                position: {
                    col_start: (mItem.position?.col_start || 1) + OFFSET.col,
                    row_start: (mItem.position?.row_start || 1) + OFFSET.row,
                },
                zIndex: maxZIndex,
            }

            mobile.itemIds.push(newId)
        }

        // Select newly duplicated group
        page.selectedItemIds = newIds
    },


    itemRotated: (
        state: StudioState,
        action: PayloadAction<{ pageId: string; itemId: string }>,
    ) => {
        const { pageId, itemId } = action.payload
        const page = getPage(state, pageId)

        if (!page) return

        const activeLayout = page.layouts[state.responsiveMode]
        const item = activeLayout.items[itemId]
        if (item) {
            const { col_span, row_span } = item.size
            item.size = { col_span: row_span, row_span: col_span }
        }

        if (state.responsiveMode === 'desktop' && page.layouts.mobile.items[itemId]) {
            const desktopItem = page.layouts.desktop.items[itemId]
            if (desktopItem) {
                page.layouts.mobile.items[itemId] =
                    createMobileLayoutItemFromDesktop(desktopItem)
            }
        }
    },
}
